/**
 * Resolve which number a contact's Call button dials and shows, falling back to the contact's
 * organization ("firm") number when the person has no direct number of their own.
 *
 * The rule the whole product agrees on: dial the person's own number when we have one; otherwise,
 * if the org they belong to has a phone on file, dial THAT and label it a "firm number" so the rep
 * knows they're reaching the company, not the person directly. Empty/whitespace values count as
 * absent.
 *
 * Every calling surface (dashboard `ClickToCallButton`, extension `ExtensionClickToCall`, the
 * `/todo/calls` power-dialer) runs this same helper so the enable/disable gate, the displayed
 * number, and the firm-number label stay in lockstep. The server's `startCall` re-resolves the same
 * fallback independently (it never trusts a client-supplied number — a toll-fraud guard), so the two
 * must agree; keeping the logic here in one place is how they do.
 */
import type { RiaPhoneKind } from '@poseidon/shared-utils/advisorUniverse';
import { phoneKindFromSource } from '@poseidon/shared-utils/advisorUniverse';

export interface ResolvedCallNumber {
  /** The number the UI shows and the Call button gates on. Empty string when neither exists. */
  phoneNumber: string;
  /** True when the number is the firm's line: the org fallback, or a personal number stored as one. */
  isFirmNumber: boolean;
  /** What the dialed number is, when its source says ('Cell' / 'Firm line' labels); null when unknown. */
  phoneKind: RiaPhoneKind | null;
}

export function resolveCallNumber(params: {
  /** The contact's own primary phone (`contact.primaryFields.phone.data`). */
  personalPhone?: string | null;
  /** Where that number came from (`contact.primaryFields.phone.source`) — tells a cell from a firm line. */
  personalPhoneSource?: string | null;
  /** The linked org's primary phone (`contactOrg.primaryFields.phone.data`). */
  orgPhone?: string | null;
}): ResolvedCallNumber {
  const personal = params.personalPhone?.trim();
  if (personal) {
    const phoneKind = phoneKindFromSource(params.personalPhoneSource);
    return { phoneNumber: personal, isFirmNumber: phoneKind === 'firm', phoneKind };
  }

  const org = params.orgPhone?.trim();
  if (org) return { phoneNumber: org, isFirmNumber: true, phoneKind: 'firm' };

  return { phoneNumber: '', isFirmNumber: false, phoneKind: null };
}
